interface Position {
  account: string;
  ticker: string;
  secType: string;
  currency: string;
  position: number;
  avgCost: number;
  mark: number | null;
  marketValue: number | null;
  unrealizedPnL: number | null;
  unrealizedPnLPct: number | null;
  dayChangePct: number | null;
}

function money(n: number, digits = 0): string {
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: digits,
  });
}

function signedMoney(n: number): string {
  return `${n >= 0 ? '+' : ''}${money(n)}`;
}

function signedPct(n: number, digits = 1): string {
  return `${n >= 0 ? '+' : ''}${n.toFixed(digits)}%`;
}

// Day P&L in $: marketValue is qty·mark(now); previous close value is
// marketValue / (1 + d). Day P&L = marketValue · d / (1 + d).
function dayPnLDollars(p: Position): number | null {
  if (p.marketValue === null || p.dayChangePct === null) return null;
  const d = p.dayChangePct / 100;
  if (d <= -1) return null;
  return (p.marketValue * d) / (1 + d);
}

interface AccountSummary {
  netLiquidation: number | null;
  totalCash: number | null;
  buyingPower: number | null;
}

export function PortfolioHealth({
  positions,
  summary,
}: {
  positions: Position[];
  summary: AccountSummary | null;
}) {
  const valued = positions.filter((p) => p.marketValue !== null && p.marketValue > 0);
  if (valued.length === 0) return null;

  const totalValue = valued.reduce((s, p) => s + (p.marketValue as number), 0);
  const totalCost = valued.reduce((s, p) => s + p.position * p.avgCost, 0);
  const totalPnL = valued.reduce((s, p) => s + (p.unrealizedPnL ?? 0), 0);
  const totalPnLPct = totalCost > 0 ? (totalPnL / totalCost) * 100 : 0;

  const dayParts = valued.map(dayPnLDollars).filter((v): v is number => v !== null);
  const dayPnL = dayParts.reduce((s, v) => s + v, 0);
  const prevTotal = totalValue - dayPnL;
  const dayPnLPct = prevTotal > 0 ? (dayPnL / prevTotal) * 100 : 0;

  const byWeight = [...valued].sort(
    (a, b) => (b.marketValue as number) - (a.marketValue as number)
  );
  const topWeightPct = ((byWeight[0].marketValue as number) / totalValue) * 100;
  const winners = valued.filter((p) => (p.unrealizedPnL ?? 0) > 0).length;
  const losers = valued.filter((p) => (p.unrealizedPnL ?? 0) < 0).length;

  const byPnLPct = [...valued].sort(
    (a, b) => (b.unrealizedPnLPct ?? 0) - (a.unrealizedPnLPct ?? 0)
  );
  const maxAbsPnLPct = Math.max(
    1,
    ...byPnLPct.map((p) => Math.abs(p.unrealizedPnLPct ?? 0))
  );

  return (
    <div className="ph">
      <div className="ph-tiles">
        {summary?.netLiquidation != null && (
          <div className="ph-tile">
            <span className="ph-tile-label">Account balance</span>
            <span className="ph-tile-value">{money(summary.netLiquidation)}</span>
            <span className="ph-tile-sub">
              cash {summary.totalCash != null ? money(summary.totalCash) : '—'}
              {summary.buyingPower != null ? ` · BP ${money(summary.buyingPower)}` : ''}
            </span>
          </div>
        )}
        <div className="ph-tile">
          <span className="ph-tile-label">Market value</span>
          <span className="ph-tile-value">{money(totalValue)}</span>
          <span className="ph-tile-sub">{valued.length} positions</span>
        </div>
        <div className="ph-tile">
          <span className="ph-tile-label">Unrealized P&L</span>
          <span className="ph-tile-value" style={{ color: totalPnL >= 0 ? 'var(--green)' : 'var(--red)' }}>
            {signedMoney(totalPnL)}
          </span>
          <span className="ph-tile-sub" style={{ color: totalPnL >= 0 ? 'var(--green)' : 'var(--red)' }}>
            {signedPct(totalPnLPct)} on {money(totalCost)} cost
          </span>
        </div>
        <div className="ph-tile">
          <span className="ph-tile-label">Day P&L</span>
          <span className="ph-tile-value" style={{ color: dayPnL >= 0 ? 'var(--green)' : 'var(--red)' }}>
            {signedMoney(dayPnL)}
          </span>
          <span className="ph-tile-sub" style={{ color: dayPnL >= 0 ? 'var(--green)' : 'var(--red)' }}>
            {signedPct(dayPnLPct, 2)} today
          </span>
        </div>
        <div className="ph-tile">
          <span className="ph-tile-label">Concentration</span>
          <span
            className="ph-tile-value"
            style={{ color: topWeightPct > 30 ? 'var(--amber)' : 'var(--text)' }}
          >
            {topWeightPct.toFixed(0)}%
          </span>
          <span className="ph-tile-sub">
            top: {byWeight[0].ticker} · {winners}W / {losers}L
          </span>
        </div>
      </div>

      <div className="ph-charts">
        <div className="ph-chart">
          <h3 className="ph-chart-title">Allocation</h3>
          {byWeight.map((p) => {
            const pct = ((p.marketValue as number) / totalValue) * 100;
            return (
              <div className="ph-row" key={`alloc-${p.ticker}-${p.secType}`}>
                <span className="ph-row-ticker">{p.ticker}</span>
                <div className="ph-track">
                  <div
                    className="ph-bar ph-bar-alloc"
                    style={{ width: `${Math.max(pct, 0.5)}%` }}
                  />
                </div>
                <span className="ph-row-value">
                  {pct.toFixed(1)}% <span className="ph-row-dim">{money(p.marketValue as number)}</span>
                </span>
              </div>
            );
          })}
        </div>

        <div className="ph-chart">
          <h3 className="ph-chart-title">Unrealized P&L %</h3>
          {byPnLPct.map((p) => {
            const pct = p.unrealizedPnLPct ?? 0;
            const half = Math.min(Math.abs(pct) / maxAbsPnLPct, 1) * 50;
            const pos = pct >= 0;
            return (
              <div className="ph-row" key={`pnl-${p.ticker}-${p.secType}`}>
                <span className="ph-row-ticker">{p.ticker}</span>
                <div className="ph-track ph-track-diverging">
                  <div
                    className={`ph-bar ${pos ? 'ph-bar-pos' : 'ph-bar-neg'}`}
                    style={
                      pos
                        ? { left: '50%', width: `${Math.max(half, 0.75)}%` }
                        : { right: '50%', width: `${Math.max(half, 0.75)}%` }
                    }
                  />
                </div>
                <span className="ph-row-value" style={{ color: pos ? 'var(--green)' : 'var(--red)' }}>
                  {signedPct(pct)}{' '}
                  <span className="ph-row-dim">{signedMoney(p.unrealizedPnL ?? 0)}</span>
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
